import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Paper,
  Button,
  Alert,
  Typography
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getSalonById } from '../utils/mockupSalons';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

// Import components
import MobileHeroSection from './salon/MobileHeroSection';
import SalonQuickInfo from './salon/SalonQuickInfo';
import ServicesList from './salon/ServicesList';
import BusinessHours from './salon/BusinessHours';
import PhotoGallery from './salon/PhotoGallery';
import ReviewsSection from './salon/ReviewsSection';
import BookingSection from './salon/BookingSection';
import FloatingBookButton from './salon/FloatingBookButton';
import ScrollDownButton from './salon/ScrollDownButton';
import SalonFooter from './salon/SalonFooter';
import DesktopSalonProfile from './DesktopSalonProfile';
import BookingDialog from './salon/BookingDialog';
import EmailVerificationBooking from './EmailVerificationBooking';
import PageLoader from './common/PageLoader';
import useIsMobile from '../hooks/useIsMobile';

dayjs.locale('fr');

const ResponsiveSalonProfile = () => {
  const { salonId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const [salon, setSalon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    service: '',
    selectedDateTime: null,
    notes: ''
  });
  const [selectedService, setSelectedService] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [emailVerificationOpen, setEmailVerificationOpen] = useState(false);
  const [pendingBookingData, setPendingBookingData] = useState(null);

  useEffect(() => {
    fetchSalonData();
  }, [salonId]);

  const fetchSalonData = async () => {
    try {
      // D'abord essayer de récupérer depuis Firebase
      const salonDoc = await getDoc(doc(db, 'users', salonId));
      
      if (salonDoc.exists()) {
        const salonData = salonDoc.data();
        
        if (!salonData.isPublic || !salonData.subscription?.active) {
          setError('Ce salon n\'est pas disponible publiquement');
          setLoading(false);
          return;
        }
        
        setSalon({
          id: salonDoc.id,
          ...salonData
        });
      } else {
        // Si pas trouvé dans Firebase, chercher dans les données mockup
        const mockupSalon = getSalonById(salonId);
        
        if (mockupSalon) {
          setSalon(mockupSalon);
        } else {
          setError('Salon non trouvé');
        }
      }
    } catch (error) {
      console.error('Error fetching salon:', error);
      
      // En cas d'erreur Firebase, essayer les données mockup
      const mockupSalon = getSalonById(salonId);
      
      if (mockupSalon) {
        setSalon(mockupSalon);
      } else {
        setError('Erreur lors du chargement du salon');
      }
    } finally {
      setLoading(false);
    }
  };

  const openBookingDialog = (service = null) => {
    const selectedSrv = service || (salon.services && salon.services.length > 0 ? salon.services[0] : null);
    const serviceName = selectedSrv ? selectedSrv.name : '';
    
    setBookingForm({
      clientName: '',
      clientEmail: '',
      clientPhone: '',
      service: serviceName,
      selectedDateTime: null,
      notes: ''
    });
    setSelectedService(selectedSrv);
    setBookingDialogOpen(true);
  };

  const handleServiceChange = (serviceName) => {
    const service = salon.services?.find(s => s.name === serviceName);
    setSelectedService(service);
    setBookingForm(prev => ({
      ...prev,
      service: serviceName,
      selectedDateTime: null
    }));
  };

  const handleTimeSlotSelect = (datetime) => {
    setBookingForm(prev => ({
      ...prev,
      selectedDateTime: datetime
    }));
  };

  const submitBooking = async () => {
    // Validation is now handled in BookingDialog component
    const bookingData = {
      salonId: salon.id,
      salonName: salon.salonName,
      clientName: bookingForm.clientName,
      clientEmail: bookingForm.clientEmail,
      clientPhone: bookingForm.clientPhone,
      service: bookingForm.service,
      date: bookingForm.selectedDateTime,
      notes: bookingForm.notes,
      createdAt: new Date()
    };

    setPendingBookingData(bookingData);
    setBookingDialogOpen(false);
    setEmailVerificationOpen(true);
  };

  const handleEmailVerificationSuccess = (message) => {
    setSuccess(message);
    setEmailVerificationOpen(false);
    setPendingBookingData(null);
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleEmailVerificationClose = () => {
    setEmailVerificationOpen(false);
    setPendingBookingData(null);
    setBookingDialogOpen(true);
  };

  const shareProfile = () => {
    if (navigator.share) {
      navigator.share({
        title: salon.salonName,
        text: `Découvrez ${salon.salonName} - ${salon.category}`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      setSuccess('Lien copié dans le presse-papiers!');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  if (loading) {
    return (
      <PageLoader
        message="Chargement du salon..."
        type="logo"
        fullScreen={true}
      />
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: isMobile ? '#F8F9FA' : 'inherit' }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button onClick={() => navigate('/')}>
            Retour à l'accueil
          </Button>
        </Container>
      </Box>
    );
  }

  if (!salon) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: isMobile ? '#F8F9FA' : 'inherit' }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          Salon non trouvé
        </Container>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
      <Box sx={{ minHeight: '100vh', backgroundColor: isMobile ? '#F8F9FA' : 'inherit', pb: isMobile ? 10 : 0 }}>
        {/* Header - Removed as requested */}

        {/* Success/Error Messages */}
        {success && (
          <Alert severity="success" sx={{ m: isMobile ? 2 : 3 }}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ m: isMobile ? 2 : 3 }}>
            {error}
          </Alert>
        )}

        {isMobile ? (
          /* Mobile Layout */
          <>
            {/* Hero Section with Animated Title */}
            <MobileHeroSection salon={salon} />

            <Container maxWidth="sm" sx={{ px: 2 }}>
              {/* Quick Info */}
              <SalonQuickInfo salon={salon} isMobile={true} />

              {/* Booking Section */}
              <BookingSection onBooking={() => openBookingDialog()} isMobile={true} />

              {/* Business Hours */}
              <BusinessHours schedule={salon.schedule} isMobile={true} />

              {/* Services */}
              <ServicesList
                services={salon.services}
                onBookService={openBookingDialog}
                isMobile={true}
              />

              {/* Photo Gallery */}
              <PhotoGallery photos={salon.photos} salonName={salon.salonName} isMobile={true} />

              {/* Reviews */}
              <ReviewsSection salon={salon} isMobile={true} />

              {/* About Section */}
              {salon.description && (
                <Paper sx={{ mt: 3, mb: 3, p: 3, borderRadius: 2, border: '1px solid #E5E7EB' }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#000000' }}>
                    À propos du salon
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280', lineHeight: 1.6 }}>
                    {salon.description}
                  </Typography>
                </Paper>
              )}

              {/* Footer Mobile */}
              <SalonFooter isMobile={true} />
            </Container>

            {/* Floating Book Button */}
            <FloatingBookButton onClick={() => openBookingDialog()} isMobile={true} />
            
            {/* Scroll Down Button */}
            <ScrollDownButton isMobile={true} />
          </>
        ) : (
          /* Desktop Layout */
          <>
            <DesktopSalonProfile salon={salon} onBooking={openBookingDialog} />
            <SalonFooter isMobile={false} />
          </>
        )}

        {/* Booking Dialog */}
        <BookingDialog
          open={bookingDialogOpen}
          onClose={() => setBookingDialogOpen(false)}
          salon={salon}
          bookingForm={bookingForm}
          setBookingForm={setBookingForm}
          selectedService={selectedService}
          onServiceChange={handleServiceChange}
          onTimeSlotSelect={handleTimeSlotSelect}
          onSubmit={submitBooking}
          isMobile={isMobile}
        />

        {/* Email Verification Dialog */}
        <EmailVerificationBooking
          open={emailVerificationOpen}
          onClose={handleEmailVerificationClose}
          onSuccess={handleEmailVerificationSuccess}
          bookingData={pendingBookingData}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default ResponsiveSalonProfile;